import { queryOptions, useQueries } from "@tanstack/react-query";
import { useDebounce } from "@reactuses/core";
import { apiClient } from "../../../../zodios/client";
import { useModuleEnabled } from "../../../../redux/selectors/modules";

const DEBOUNCE_MS = 500;

// The plan key holding the name the server already has for each campaign-name field, so editing a
// saved plan doesn't report its own campaigns as taken.
const SAVED_NAME_KEY: Partial<Record<string, string>> = {
  auto_name: "savedAutoCampaignName",
  manual_name: "savedManualCampaignName",
  contextual_name: "savedContextualCampaignName",
  audience_name: "savedAudienceCampaignName",
  walmart_manual_campaign_name: "savedWalmartManualCampaignName",
  walmart_auto_campaign_name: "savedWalmartAutoCampaignName",
};

type CampaignNames = Record<string, string | null | undefined>;

type CampaignNamePlan = {
  channel?: string;
  canHaveChildren?: boolean;
  campaignNames?: CampaignNames;
  children?: { campaignNames?: CampaignNames; advStatus?: number }[];
  [savedNameKey: string]: unknown;
};

/** Whether `name`, typed into the `campaignType` field of `plan`, needs an availability lookup. */
export function needsCampaignNameCheck(
  plan: CampaignNamePlan,
  campaignType: string,
  name: string | null | undefined,
): name is string {
  if (!name || plan.channel !== "AMZ") {
    return false;
  }
  const savedKey = SAVED_NAME_KEY[campaignType];
  return savedKey === undefined || plan[savedKey] !== name;
}

/** The campaign names a plan will submit that have to be checked against the channel's campaigns. */
export function campaignNamesToCheck(plan: CampaignNamePlan): string[] {
  // A segmented plan's campaigns are named per component; archived components (advStatus 4) are hidden.
  const entries =
    plan.canHaveChildren ?
      (plan.children ?? [])
        .filter((child) => child.advStatus !== 4)
        .flatMap((child) => Object.entries(child.campaignNames ?? {}))
    : Object.entries(plan.campaignNames ?? {});
  return entries.flatMap(([campaignType, name]) => (needsCampaignNameCheck(plan, campaignType, name) ? [name] : []));
}

const campaignNameTakenQuery = (marketplace: string, name: string) =>
  queryOptions({
    queryKey: [{ entity: "channelCampaignNameTaken", marketplace, name }],
    // The endpoint matches names exactly and leaves out archived campaigns.
    queryFn: async () => (await apiClient.listChannelCampaigns({ queries: { marketplace, q: name } })).length > 0,
    staleTime: 60 * 1000,
  });

type CampaignNameChecks = {
  /** Names without an answer yet: still being typed, or the lookup is in flight. */
  checking: ReadonlySet<string>;
  /** Names an existing campaign on the channel already uses. */
  taken: ReadonlySet<string>;
  /** Names whose lookup failed. */
  failed: ReadonlySet<string>;
};

/**
 * Looks each name up against the channel's existing campaigns. The answers live in the query cache,
 * keyed by name, so the plan (to gate its steps) and each field (to show its own status) can both
 * call this for the names they care about and read the same lookups.
 */
export function useCampaignNameChecks(names: readonly string[], marketplace: string): CampaignNameChecks {
  const enabled = useModuleEnabled("CAMPAIGN_NAMES");
  const uniqueNames = enabled ? [...new Set(names)] : [];
  // A name is only looked up once it has stopped changing, so typing doesn't send a request per
  // keystroke; until then it counts as checking.
  const settledNames = JSON.parse(useDebounce(JSON.stringify(uniqueNames), DEBOUNCE_MS)) as string[];

  const results = useQueries({
    queries: uniqueNames.map((name) => ({
      ...campaignNameTakenQuery(marketplace, name),
      enabled: settledNames.includes(name),
    })),
  });

  const checks = { checking: new Set<string>(), taken: new Set<string>(), failed: new Set<string>() };
  uniqueNames.forEach((name, i) => {
    const result = results[i];
    if (result?.data !== undefined) {
      if (result.data) checks.taken.add(name);
    } else if (result?.isError) {
      checks.failed.add(name);
    } else {
      checks.checking.add(name);
    }
  });
  return checks;
}
